import React, { useState, useEffect, useRef } from "react";
import "./admin.css";
import html2canvas from "html2canvas";
import axios from "axios";
import * as XLSX from "xlsx";
import  API_BASE  from "../api/api";

const ViewOrder = () => {
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [showTrackingModal, setShowTrackingModal] = useState(false);
  const [currentOrderId, setCurrentOrderId] = useState(null);
  const [currentUserName, setCurrentUserName] = useState("");
  const [currentUserAddress, setCurrentUserAddress] = useState("");
  const [trackingInput, setTrackingInput] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("all");
  const tableRef = useRef();

  /* ================= FETCH ORDERS ================= */
  useEffect(() => {
    fetchOrders();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [orders, statusFilter, dateFilter]);

  const fetchOrders = () => {
    axios.get(`${API_BASE}/getorder.php?limit=500`, {
  withCredentials: true
})
      .then((res) => {
if (res.data && res.data.status === "success" && Array.isArray(res.data.data)) {
  setOrders(res.data.data); // 🔥 FIX
} else {
  setOrders([]);
}
      })
      .catch((err) => {
        console.error("Fetch orders error:", err);
        setOrders([]);
      });
  };

  /* ================= FILTERS ================= */
  const applyFilters = () => {
    let filtered = [...orders];

    if (statusFilter !== "all") {
      filtered = filtered.filter((o) =>
        statusFilter === "shipped" ? !!o.tracking_id : !o.tracking_id
      );
    }

    const now = new Date();
    filtered = filtered.filter((o) => {
      if (!o.created_at) return true;
      const d = new Date(o.created_at);

      if (dateFilter === "today") {
        return d.toDateString() === now.toDateString();
      }
      if (dateFilter === "lastWeek") {
        const lw = new Date();
        lw.setDate(now.getDate() - 7);
        return d >= lw && d <= now;
      }
      if (dateFilter === "lastMonth") {
        const lm = new Date();
        lm.setMonth(now.getMonth() - 1);
        return d >= lm && d <= now;
      }
      return true;
    });

    setFilteredOrders(filtered);
  };

  /* ================= SAFE ITEMS PARSER ================= */
  const parseItems = (raw) => {
    try {
      if (Array.isArray(raw)) return raw;
      if (typeof raw === "string") {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
      }
      return [];
    } catch {
      return [];
    }
  };

  /* ================= EXPORT ================= */
  const downloadPDF = () => {
    if (!tableRef.current) return;
    html2canvas(tableRef.current).then((canvas) => {
      const img = canvas.toDataURL("image/png");
      const pdf = new window.jsPDF("l", "mm", "a4");
      const w = pdf.internal.pageSize.getWidth();
      const h = (canvas.height * w) / canvas.width;
      pdf.addImage(img, "PNG", 0, 0, w, h);
      pdf.save("orders.pdf");
    });
  };

  const downloadExcel = () => {
    const data = filteredOrders.map((o) => {
      const items = parseItems(o.items);
      return {
        ID: o.id,
        Name: o.name,
        Phone: o.phone,
        Address:
          `${o.address}` +
          (o.landmark ? `, ${o.landmark}` : "") +
          (o.locality ? `, ${o.locality}` : "") +
          `, ${o.city}, ${o.state} - ${o.pincode}`,
        Items: items.map((i) => `${i.name} × ${i.quantity}`).join(", "),
        Total: o.total_amount,
        Status: o.tracking_id ? "Shipped" : "Unshipped",
      };
    });

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Orders");
    XLSX.writeFile(wb, "orders.xlsx");
  };

  /* ================= TRACKING ================= */
  const openTrackingModal = (order) => {
    const fullAddress =
      `${order.address}` +
      (order.landmark ? `, ${order.landmark}` : "") +
      (order.locality ? `, ${order.locality}` : "") +
      `, ${order.city}, ${order.state} - ${order.pincode}`;

    setCurrentOrderId(order.id);
    setTrackingInput(order.tracking_id || "");
    setCurrentUserName(order.name || "");
    setCurrentUserAddress(fullAddress);
    setShowTrackingModal(true);
  };

  const saveTrackingId = () => {
    const val = trackingInput.trim();
    const pattern = /^[A-Za-z0-9-_]{3,30}$/;

    if (!val) return alert("Tracking ID cannot be empty");
    if (!pattern.test(val)) return alert("Invalid Tracking ID");

    axios
      .post(
        `${API_BASE}/tracking.php`,
        { id: currentOrderId, tracking_id: val },
        { withCredentials: true }
      )
      .then((res) => {
        if (res.data && res.data.status === "success") {
          setOrders((prev) =>
            prev.map((o) =>
              o.id === currentOrderId ? { ...o, tracking_id: val } : o
            )
          );
          setShowTrackingModal(false);
          setTrackingInput("");
          setCurrentUserName("");
          setCurrentUserAddress("");
        }
      })
      .catch(console.error);
  };

  const [recovering, setRecovering] = useState(false);

  const handleAutoRecover = () => {
    setRecovering(true);
    axios
      .get(`${API_BASE}/recover_orders.php`, { withCredentials: true })
      .then((res) => {
        if (res.data && res.data.status === "success") {
          alert(res.data.message);
          fetchOrders();
        } else {
          alert(res.data?.message || "Recovery check failed");
        }
      })
      .catch((err) => {
        console.error("Auto recover error:", err);
        alert("Failed to run auto recovery check");
      })
      .finally(() => {
        setRecovering(false);
      });
  };

  /* ================= RENDER ================= */
  return (
    <div className="viewbook-container" style={{ fontSize: "15px" }}>
      <h1 className="dashboard-title" style={{ fontSize: "22px" }}>
        All Orders
      </h1>

      <div className="filters" style={{ gap: "10px", marginBottom: "14px", flexWrap: "wrap" }}>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="all">All Status</option>
          <option value="shipped">Shipped</option>
          <option value="unshipped">Unshipped</option>
        </select>

        <select value={dateFilter} onChange={(e) => setDateFilter(e.target.value)}>
          <option value="all">All Dates</option>
          <option value="today">Today</option>
          <option value="lastWeek">Last Week</option>
          <option value="lastMonth">Last Month</option>
        </select>

        <button className="cardd blue" onClick={downloadPDF}>
          Download PDF
        </button>
        <button className="cardd green" onClick={downloadExcel}>
          Download Excel
        </button>
        <button
          className="cardd"
          style={{
            backgroundColor: "#e67e22",
            color: "#ffffff",
            cursor: recovering ? "not-allowed" : "pointer"
          }}
          onClick={handleAutoRecover}
          disabled={recovering}
        >
          {recovering ? "Checking Razorpay..." : "Auto Recover Orders"}
        </button>
      </div>

      <div className="table-wrapper" ref={tableRef}>
        <table className="book-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Name</th>
              <th>Phone</th>
              <th>Address</th>
              <th>Items</th>
              <th>Total</th>
              <th>Status</th>
              <th>Tracking / Invoice</th>
            </tr>
          </thead>
          <tbody>
            {filteredOrders.map((o) => {
              const items = parseItems(o.items);

              return (
                <tr key={o.id}>
                  <td>{o.id}</td>
                  <td>{o.name}</td>
                  <td>{o.phone}</td>

                  <td>
                    {o.address}
                    {o.landmark ? `, ${o.landmark}` : ""}
                    {o.locality ? `, ${o.locality}` : ""}
                    {`, ${o.city}, ${o.state} - ${o.pincode}`}
                  </td>

                  <td>
                    <ul>
                      {items.length > 0 ? (
                        items.map((i, idx) => (
                          <li key={idx}>
                            {i.name} × {i.quantity}
                          </li>
                        ))
                      ) : (
                        <li style={{ color: "#999" }}>No items data</li>
                      )}
                    </ul>
                  </td>

                  <td>₹{o.total_amount}</td>

                  <td>
                    <span
                      style={{
                        display: "inline-block",
                        padding: "6px 12px",
                        borderRadius: "6px",
                        fontWeight: "600",
                        fontSize: "13px",
                        color: "#fff",
                        backgroundColor: o.tracking_id ? "#2e7d32" : "#c62828",
                      }}
                    >
                      {o.tracking_id ? "Shipped" : "Unshipped"}
                    </span>
                  </td>

                  <td>
                    <button className="cardd blue" onClick={() => openTrackingModal(o)}>
                      {o.tracking_id || "Add Tracking ID"}
                    </button>

                    <button
                      className="cardd green"
                      onClick={() =>
window.open(
  `${API_BASE}/invoice.php?id=${o.id}&admin=1`,
  "_blank"
)
                      }
                    >
                      Invoice
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showTrackingModal && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ width: "420px", padding: "22px" }}>
            <h3>Enter Tracking ID</h3>

            <div style={{ marginBottom: "8px", fontWeight: 600 }}>
              Order for: <span style={{ color: "#2874F0" }}>{currentUserName}</span>
            </div>

            <div style={{ fontSize: "14px", marginBottom: "12px" }}>
              {currentUserAddress}
            </div>

            <input
              type="text"
              value={trackingInput}
              onChange={(e) => setTrackingInput(e.target.value)}
              placeholder="Tracking ID"
              style={{ width: "100%", padding: "10px" }}
            />

            <div style={{ marginTop: "14px", display: "flex", gap: "10px" }}>
              <button className="cardd blue" onClick={saveTrackingId}>
                Save
              </button>
              <button className="cardd green" onClick={() => setShowTrackingModal(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewOrder;
